/**
 * Owner: Masa Bayu
 * File: qx-draft-dropcap.ts
 * Date:Time: 2026-10-16 01:21
 * Description: Drop cap · letter span font (≠ body / execCommand).
 */

import { DEFAULT_DROPCAP_FONT } from './qx-draft-fonts';

export const DRAFT_DROPCAP_CLASS = 'qxw-dropcap';
export const DRAFT_DROPCAP_ATTR = 'data-qx-dropcap';
export const DRAFT_DROPCAP_FONT_ATTR = 'data-qx-dropcap-font';
export const DRAFT_DROPCAP_FONT_VAR = '--qxw-dropcap-font';
export const DRAFT_DROPCAP_LETTER_CLASS = 'qxw-dropcap-letter';

const BLOCK_TAGS = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'ul',
  'ol',
  'blockquote',
  'pre',
  'figure',
  'img',
]);

/** Last caret paragraph · survives <select> focus steal. */
let capturedDropcapBlock: HTMLElement | null = null;

function closestParagraph(node: Node | null): HTMLElement | null {
  let current: Node | null = node;
  while (current && current !== document.body) {
    if (current instanceof HTMLElement) {
      const tag = current.tagName.toLowerCase();
      if (tag === 'p' || tag === 'div') {
        if (current.isContentEditable === false) return null;
        if (current.classList.contains('qxw-page__editor')) return null;
        return current;
      }
      if (tag === 'li' || tag === 'h1' || tag === 'h2' || tag === 'h3') return null;
    }
    current = current.parentNode;
  }
  return null;
}

function paragraphFromSelection(): HTMLElement | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  return closestParagraph(selection.getRangeAt(0).commonAncestorContainer);
}

export function paragraphHasDropcap(el: HTMLElement): boolean {
  return (
    el.classList.contains(DRAFT_DROPCAP_CLASS) || el.getAttribute(DRAFT_DROPCAP_ATTR) === '1'
  );
}

/** Call on dropcap-font control mousedown · before editor blurs. */
export function captureDraftDropcapTarget(): void {
  const block = paragraphFromSelection();
  if (block) capturedDropcapBlock = block;
}

function resolveDropcapBlock(): HTMLElement | null {
  if (capturedDropcapBlock?.isConnected) return capturedDropcapBlock;
  const fromSel = paragraphFromSelection();
  if (fromSel) {
    capturedDropcapBlock = fromSel;
    return fromSel;
  }
  const existing = document.querySelector(
    `.qxw-page__editor.${DRAFT_DROPCAP_CLASS}, .qxw-page__editor .${DRAFT_DROPCAP_CLASS}`,
  );
  return existing instanceof HTMLElement ? existing : null;
}

function firstContentText(block: HTMLElement): Text | null {
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (parent?.closest('figcaption, script, style')) return NodeFilter.FILTER_REJECT;
      const text = node.textContent ?? '';
      if (!/\S/.test(text)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  return walker.nextNode() as Text | null;
}

function unwrapDropcapLetter(block: HTMLElement): void {
  for (const el of Array.from(block.querySelectorAll(`.${DRAFT_DROPCAP_LETTER_CLASS}`))) {
    const parent = el.parentNode;
    if (!parent) continue;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
    if (parent instanceof HTMLElement) parent.normalize();
  }
}

/** Wrap / refresh the single drop-cap letter · !important beats body + fontName. */
function paintDropcapLetter(block: HTMLElement, family: string): void {
  const existing = block.querySelector(`.${DRAFT_DROPCAP_LETTER_CLASS}`);
  if (existing instanceof HTMLElement) {
    existing.style.setProperty('font-family', family, 'important');
    return;
  }
  const text = firstContentText(block);
  if (!text?.textContent) return;
  const content = text.textContent;
  const match = content.match(/^(\s*)(\S)/u);
  if (!match) return;
  const lead = match[1] ?? '';
  const ch = match[2]!;
  const rest = content.slice(lead.length + ch.length);
  const span = document.createElement('span');
  span.className = DRAFT_DROPCAP_LETTER_CLASS;
  span.setAttribute('data-qx-dropcap-letter', '1');
  span.style.setProperty('font-family', family, 'important');
  span.textContent = ch;
  const parent = text.parentNode;
  if (!parent) return;
  if (lead) parent.insertBefore(document.createTextNode(lead), text);
  parent.insertBefore(span, text);
  if (rest.length > 0) text.textContent = rest;
  else parent.removeChild(text);
}

function paintDropcapFont(el: HTMLElement, family: string | null): void {
  if (!family || family.trim().length === 0) {
    el.removeAttribute(DRAFT_DROPCAP_FONT_ATTR);
    el.style.removeProperty(DRAFT_DROPCAP_FONT_VAR);
    unwrapDropcapLetter(el);
    return;
  }
  const value = family.trim();
  el.setAttribute(DRAFT_DROPCAP_FONT_ATTR, value);
  el.style.setProperty(DRAFT_DROPCAP_FONT_VAR, value);
  paintDropcapLetter(el, value);
}

function setDropcap(el: HTMLElement, on: boolean, fontFamily?: string | null): void {
  el.classList.toggle(DRAFT_DROPCAP_CLASS, on);
  if (on) {
    el.setAttribute(DRAFT_DROPCAP_ATTR, '1');
    const existing = el.getAttribute(DRAFT_DROPCAP_FONT_ATTR);
    const next = fontFamily?.trim() || existing || DEFAULT_DROPCAP_FONT.family;
    paintDropcapFont(el, next);
  } else {
    el.removeAttribute(DRAFT_DROPCAP_ATTR);
    paintDropcapFont(el, null);
  }
}

/** Re-apply letter fonts after body fontName / --qxw-body changes. */
export function reassertDraftDropcapLetterFonts(root?: ParentNode | null): void {
  const scope = root ?? document;
  for (const el of Array.from(scope.querySelectorAll(`.${DRAFT_DROPCAP_CLASS}`))) {
    if (!(el instanceof HTMLElement) || !paragraphHasDropcap(el)) continue;
    const family =
      el.getAttribute(DRAFT_DROPCAP_FONT_ATTR)?.trim() || DEFAULT_DROPCAP_FONT.family;
    paintDropcapLetter(el, family);
  }
}

/** Toggle drop cap on the paragraph at the caret. */
export function runDraftDropcap(force?: boolean): boolean {
  const block = paragraphFromSelection() ?? resolveDropcapBlock();
  if (!block) return false;
  capturedDropcapBlock = block;
  const next = force ?? !paragraphHasDropcap(block);
  setDropcap(block, next);
  return true;
}

/**
 * Set drop-cap letter font only · never body / selection fontName.
 * Uses captured target when the font <select> steals focus.
 */
export function runDraftDropcapFont(family: string): boolean {
  const value = family.trim();
  if (!value) return false;
  const block = resolveDropcapBlock();
  if (!block) return false;
  capturedDropcapBlock = block;
  if (!paragraphHasDropcap(block)) setDropcap(block, true, value);
  else paintDropcapFont(block, value);
  return true;
}

export function readDraftDropcapFont(el?: HTMLElement | null): string | null {
  const block = el ?? resolveDropcapBlock();
  if (!block || !paragraphHasDropcap(block)) return null;
  return block.getAttribute(DRAFT_DROPCAP_FONT_ATTR) || DEFAULT_DROPCAP_FONT.family;
}

/**
 * Block indexes with drop cap — same enumeration order as draftSyncFromEditor.
 * Session hydrate only · ≠ QXW schema invent.
 */
export function collectDropcapBlockIndexes(root: HTMLElement): number[] {
  const indexes: number[] = [];
  let blockIndex = 0;
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = (child.textContent ?? '').replace(/\u00a0/g, ' ');
      if (text.trim().length === 0) continue;
      blockIndex += 1;
      continue;
    }
    if (!(child instanceof HTMLElement)) continue;
    const tag = child.tagName.toLowerCase();
    if (!BLOCK_TAGS.has(tag)) continue;
    if ((tag === 'p' || tag === 'div') && paragraphHasDropcap(child)) {
      indexes.push(blockIndex);
    }
    blockIndex += 1;
  }
  return indexes;
}

/** Font stacks keyed by the same block indexes as collectDropcapBlockIndexes. */
export function collectDropcapFontsByBlockIndex(
  root: HTMLElement,
): Record<number, string> {
  const fonts: Record<number, string> = {};
  let blockIndex = 0;
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = (child.textContent ?? '').replace(/\u00a0/g, ' ');
      if (text.trim().length === 0) continue;
      blockIndex += 1;
      continue;
    }
    if (!(child instanceof HTMLElement)) continue;
    const tag = child.tagName.toLowerCase();
    if (!BLOCK_TAGS.has(tag)) continue;
    if ((tag === 'p' || tag === 'div') && paragraphHasDropcap(child)) {
      const font = child.getAttribute(DRAFT_DROPCAP_FONT_ATTR);
      if (font && font.trim()) fonts[blockIndex] = font.trim();
    }
    blockIndex += 1;
  }
  return fonts;
}

/** Apply session dropcap indexes + per-block fonts (same order as draftSyncFromEditor). */
export function applyDraftDropcaps(
  root: HTMLElement,
  indexes: readonly number[] = [],
  fontsByBlockIndex: Readonly<Record<number, string>> = {},
): void {
  const want = new Set(indexes);
  for (const el of Array.from(root.querySelectorAll(`.${DRAFT_DROPCAP_CLASS}`))) {
    if (el instanceof HTMLElement) setDropcap(el, false);
  }
  let blockIndex = 0;
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = (child.textContent ?? '').replace(/\u00a0/g, ' ');
      if (text.trim().length === 0) continue;
      blockIndex += 1;
      continue;
    }
    if (!(child instanceof HTMLElement)) continue;
    const tag = child.tagName.toLowerCase();
    if (!BLOCK_TAGS.has(tag)) continue;
    if ((tag === 'p' || tag === 'div') && want.has(blockIndex)) {
      setDropcap(child, true, fontsByBlockIndex[blockIndex] ?? null);
    }
    blockIndex += 1;
  }
}
